import { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  createFilterOptions,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  Check,
  CheckBox,
  CheckBoxOutlineBlank,
  Delete,
  MoreHoriz,
} from "@mui/icons-material";

// Paleta de cores reaproveitada do recurso de grupos da Flow (blocosEasyFlow)
export const TAG_COLORS = [
  "chip.main.pending",
  "chip.main.completed",
  "chip.main.inProgress",
  "chip.main.waitingReview",
  "chip.main.waitingReturn",
  "chip.main.arquive",
];

const COLOR_NAMES = ["Vermelho", "Verde", "Laranja", "Azul", "Roxo", "Cinza"];

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

/**
 * Seletor de tags de uma interface: exibe os chips coloridos atribuídos e um
 * botão "+" que abre o menu de atribuição (buscar/criar/editar/excluir tags).
 * Espelha o comportamento dos grupos da Flow, mas por interface.
 */
export default function InterfaceTagSelector({
  tags = [],
  interfaceTags = [],
  editMode = false,
  disabled = false,
  onAssign = () => {},
  onUnassign = () => {},
  onCreateTag = async () => null,
  onEditTag = () => {},
  onDeleteTag = () => {},
}) {
  const theme = useTheme();
  const filter = createFilterOptions();

  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const [editAnchorEl, setEditAnchorEl] = useState(null);
  const [editing, setEditing] = useState(null);

  const open = Boolean(anchorEl);
  const openEdit = Boolean(editAnchorEl);

  // Resolve as tags atribuídas pelas definições atuais (nome/cor ao vivo) e
  // descarta as que foram excluídas.
  const assignedIds = new Set((tags || []).map((t) => t?.id));
  const resolvedAssigned = (tags || [])
    .map((t) => interfaceTags.find((d) => d?.id === t?.id))
    .filter(Boolean);

  const term = (search || "").trim().toLowerCase();
  const filteredDefs = interfaceTags.filter((t) =>
    (t?.tagName || "").toLowerCase().includes(term),
  );
  const exactExists = interfaceTags.some(
    (t) => (t?.tagName || "").trim().toLowerCase() === term,
  );

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const handleCloseEdit = () => {
    setEditAnchorEl(null);
    setEditing(null);
  };

  const handleToggle = (tag) => {
    if (assignedIds.has(tag.id)) onUnassign(tag);
    else onAssign(tag);
  };

  const handleCreate = async (name) => {
    if (!name?.trim()) return;
    const created = await onCreateTag(name.trim());
    if (created) {
      onAssign(created);
      setSearch("");
    }
  };

  const chipColor = theme.palette.colors?.white?.lighter || "#fff";

  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
      {resolvedAssigned.map((tag) => (
        <Chip
          key={tag.id}
          size="small"
          label={tag.tagName}
          onDelete={editMode ? () => onUnassign(tag) : undefined}
          sx={{
            height: 20,
            bgcolor: tag.colorTag,
            color: chipColor,
            "& .MuiChip-deleteIcon": { color: chipColor },
          }}
        />
      ))}

      <Tooltip title="Adicionar tag">
        <span>
          <IconButton
            size="small"
            disabled={!editMode || disabled}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ p: 0.25 }}
          >
            <Add fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Menu de atribuição */}
      <Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
        <Box sx={{ px: 1, pt: 0.5, width: 260 }}>
          <Autocomplete
            freeSolo
            multiple
            size="small"
            disableCloseOnSelect
            clearIcon={null}
            options={[]}
            value={[]}
            inputValue={search}
            onInputChange={(event, value) => setSearch(value)}
            filterOptions={(options, params) => {
              filter(options, params);
              setSearch(params.inputValue);
              return [];
            }}
            getOptionLabel={() => ""}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Enter" && term && !exactExists) {
                handleCreate(search);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                placeholder="Buscar ou criar tag"
              />
            )}
          />
        </Box>

        <Divider sx={{ my: 0.5 }} />

        <Box sx={{ maxHeight: 240, overflowY: "auto", width: 260 }}>
          {filteredDefs.length === 0 && !term && (
            <Typography
              variant="caption"
              sx={{ display: "block", textAlign: "center", py: 1, opacity: 0.7 }}
            >
              Nenhuma tag criada. Digite para criar.
            </Typography>
          )}

          {filteredDefs.map((tag) => (
            <MenuItem
              key={tag.id}
              onClick={() => handleToggle(tag)}
              sx={{ py: 0.25 }}
            >
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={assignedIds.has(tag.id)}
                size="small"
                sx={{ mr: 0.5 }}
                onClick={(e) => e.stopPropagation()}
                onChange={() => handleToggle(tag)}
              />
              <Chip
                size="small"
                label={tag.tagName}
                sx={{ height: 20, bgcolor: tag.colorTag, color: chipColor }}
              />
              <IconButton
                size="small"
                sx={{ ml: "auto" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing({ ...tag });
                  setEditAnchorEl(e.currentTarget);
                }}
              >
                <MoreHoriz fontSize="small" />
              </IconButton>
            </MenuItem>
          ))}

          {term && !exactExists && (
            <MenuItem onClick={() => handleCreate(search)}>
              <Add fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="body2">Criar "{search.trim()}"</Typography>
            </MenuItem>
          )}
        </Box>
      </Menu>

      {/* Submenu de edição de tag */}
      <Menu anchorEl={editAnchorEl} open={openEdit} onClose={handleCloseEdit}>
        {editing && (
          <Box sx={{ width: 240, p: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={editing.tagName || ""}
              error={!editing.tagName?.trim()}
              helperText={!editing.tagName?.trim() ? "Nome obrigatório" : ""}
              onChange={(e) =>
                setEditing((prev) => ({ ...prev, tagName: e.target.value }))
              }
              onKeyDown={(e) => e.stopPropagation()}
            />
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" sx={{ opacity: 0.7, pl: 0.5 }}>
              Cor
            </Typography>
            {TAG_COLORS.map((color, idx) => (
              <MenuItem
                key={color}
                onClick={() =>
                  setEditing((prev) => ({ ...prev, colorTag: color }))
                }
                sx={{ py: 0.25 }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "4px",
                    bgcolor: color,
                    mr: 1,
                  }}
                />
                <Typography variant="body2">{COLOR_NAMES[idx]}</Typography>
                {editing.colorTag === color && (
                  <ListItemIcon sx={{ ml: "auto", minWidth: 0 }}>
                    <Check fontSize="small" />
                  </ListItemIcon>
                )}
              </MenuItem>
            ))}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Button
                size="small"
                color="error"
                startIcon={<Delete />}
                onClick={() => {
                  onDeleteTag(editing.id);
                  handleCloseEdit();
                }}
              >
                Excluir
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={!editing.tagName?.trim()}
                onClick={() => {
                  onEditTag(editing);
                  handleCloseEdit();
                }}
              >
                Salvar
              </Button>
            </Box>
          </Box>
        )}
      </Menu>
    </Box>
  );
}
